import { formatPriceWithCurrency } from '@toggle/helpers';
import { AssetClassCode, formatTicker } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ConditionSection } from '~/components/condition-section/ConditionSection';
import { EntityHeader } from '~/modules/entity-header/EntityHeader';
import {
  formatUtcDate,
  getInsightCondition,
} from '~/services/insights/utils/insight-utils';
import { InsightWidgetData } from '~/views/alfred/quick-analysis/use-chat/assistant-response.type';
import { AssessmentWidget } from '~/widgets/assessment-widget/AssessmentWidget';
import { InsightSummary } from '~/widgets/insight-widget/components/insight-summary/InsightSummary';
import { InsightPastEpisodes } from '~/widgets/past-episodes-widget/components/insight-past-episodes/InsightPastEpisodes';

import { InsightsReaction } from '../insights-reaction/InsightsReaction';
import { ScenarioTriggerSection } from '../scenario-trigger-section/ScenarioTriggerSection';
import { InsightChartSection } from './InsightChartSection';
import styles from './InsightsDrawerContent.module.scss';

export type LegacyInsightsDrawerContentProps = {
  insightsData: InsightWidgetData;
  header?: () => React.JSX.Element;
  showEntityHeader?: boolean;
};

export const LegacyInsightsDrawerContent = ({
  insightsData,
  header,
  showEntityHeader = true,
}: LegacyInsightsDrawerContentProps) => {
  const { t } = useTranslation(['scenarioCommander', 'widget']);
  const { insight, entity, snakeTs } = insightsData;

  const createdAtDate = formatUtcDate(insight.created_at, 'PP');
  const lastValueDate = insight.metadata.last_value_date
    ? formatUtcDate(insight.metadata.last_value_date, 'PP')
    : undefined;
  const entityName = entity.name;
  const isFixedIncome =
    entity.asset_class === AssetClassCode.Fi && entity.class === 6;

  const episodeCount = insight.episodes.length;
  const condition = getInsightCondition(insight, t);

  return (
    <div className={styles.container}>
      <div className={styles.insightTopWrapper}>
        {showEntityHeader && (
          <EntityHeader
            entity={entity}
            truncateLongTitle
            truncateAtLength={45}
            isDrawer
            headerButton={<></>}
          />
        )}
        {header && header()}
        <InsightSummary insight={insight} isFixedIncome={isFixedIncome} />
        {lastValueDate && (
          <p className={styles.valueInfo}>
            {t('widget:insights:analysisBased', {
              price: formatPriceWithCurrency(
                insight.metadata.last_value,
                insight.metadata.currency
              ),
              createdAtDate: createdAtDate,
              lastValueDate: lastValueDate,
              ticker: formatTicker(entity),
            })}
          </p>
        )}
        <InsightsReaction
          entityName={entityName}
          episodeCount={episodeCount}
          isFixedIncome={isFixedIncome}
        />
        <InsightChartSection
          insightId={insight.id}
          bestHorizon={insight.metadata.horizon}
          isFixedIncome={isFixedIncome}
          snakeTs={snakeTs}
        />
      </div>
      <ScenarioTriggerSection
        entityName={entityName}
        entityTag={entity.tag}
        conditions={insight.conditions}
      />
      <div className={styles.deepDiveWrapper}>
        <div className={styles.styledTitle}>
          {t('scenarioCommander:deepDive')}
        </div>
        <ConditionSection condition={condition} />
        <InsightPastEpisodes
          insight={insight}
          entity={entity}
          variant={'insight-past-episodes'}
        />
      </div>
      <div>
        <AssessmentWidget
          isFixedIncome={isFixedIncome}
          gaugeValue={insight.metadata.stars}
          horizon={insight.metadata.horizon}
          median={insight.metadata.median_return}
          checklist={insight.metadata.high_conviction_report}
          hasHeader={false}
          titleElement={
            <div className={styles.styledTitle}>
              {t('scenarioCommander:scenarioQuality')}
            </div>
          }
        />
      </div>
    </div>
  );
};
